/**
 * Ein- und Ausbuchen mit Auswahl (ADR 0020).
 *
 * Statt jeden Scan sofort zu buchen, wird **angekreuzt** -- in der Liste
 * darunter oder mit dem Scanner. Gebucht wird erst mit „Fertig“, alles in
 * **einem** Vorgang, mit einer Bestätigung, welche Geräte wohin gingen, und
 * „Rückgängig“ für alles zusammen (so vom Nutzer entschieden). Ein Buchungs-
 * moment statt vieler: weniger Fehlbuchungen.
 *
 * - **Ausbuchen:** die Liste ist der Inhalt des Behälters. Das Ziel steht
 *   oben -- das zuletzt gewählte dieses Benutzers, sonst „Ohne festen Platz“
 *   (ADR 0013 §3). Ein Lagerort-QR wählt ein neues Ziel.
 * - **Einbuchen:** die Liste ist, was hierher gehört und woanders ist
 *   (Stammplatz hier). Dazu, auf Wunsch, „… und als Stammplatz festlegen“.
 *
 * Ein gescannter Artikel, der nicht in der Liste steht, kommt angekreuzt
 * dazu, mit gelbem Hinweis, wo er war -- nicht blockieren (ADR 0013 §3).
 * Alles aus der lokalen Kopie, auch ohne Netz.
 */

import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import type { Ziel } from "@thw/core-domain";
import { ausbuchenAuswahl, einbuchenAuswahl, type AuswahlZeile } from "../lokal/auswahl.js";
import { bestandLaden, useBestand } from "../lokal/bestand.js";
import { stammplatzSetzen } from "../lokal/stammplatz.js";
import {
  neuerVorgang,
  ortDesArtikels,
  schonDort,
  verboten,
  verschieben,
  woWar,
  zielName,
  type Gebucht,
} from "../lokal/verschieben.js";
import { aufloesenIn, codeAufloesen } from "../scan/aufloesen.js";
import { Scanner, rueckmeldungGeben } from "../scan/Scanner.jsx";
import {
  Knopf,
  Ladeanzeige,
  Nummer,
  Rueckmeldung,
  Schalter,
  type MeldungsArt,
} from "./bausteine.jsx";
import { EtikettHinweis } from "./EtikettHinweis.jsx";
import { GeraetAnlegen } from "./GeraetAnlegen.jsx";
import {
  UnbekannterCodeText,
  unbekanntErfassen,
  unbekanntKopf,
  type Unbekannt,
} from "./UnbekannterCode.jsx";
import { Wohin } from "./Wohin.jsx";

/** So viele Geräte stehen einzeln in der Bestätigung, der Rest als Zahl. */
const NAMEN_IN_BESTAETIGUNG = 8;

export interface AuswahlBuchenEigenschaften {
  lagerort_id: string;
  art: "einbuchen" | "ausbuchen";
  /** Beim Ausbuchen: wohin. `zuletzt` sagt, ob es das gemerkte Ziel ist. */
  ziel: { ziel: Ziel; zuletzt: boolean } | null;
  aufZiel: (ziel: Ziel) => void;
  online: boolean;
  aufAbbruch: () => void;
  aufGebucht: (ergebnis: {
    art: MeldungsArt;
    kopf: string;
    zusatz: ReactNode;
    gebucht: Gebucht[];
  }) => void;
}

interface Zusatz extends AuswahlZeile {
  /** Warum gelb: „war in WERKST“. */
  hinweis: string;
}

export function AuswahlBuchen({
  lagerort_id,
  art,
  ziel,
  aufZiel,
  online,
  aufAbbruch,
  aufGebucht,
}: AuswahlBuchenEigenschaften) {
  const bestand = useBestand();
  const [gewaehlt, setGewaehlt] = useState<ReadonlySet<string>>(new Set());
  const [dazu, setDazu] = useState<Zusatz[]>([]);
  const [meldung, setMeldung] = useState<{
    art: MeldungsArt;
    kopf: string;
    zusatz?: ReactNode;
  } | null>(null);
  const [alsStammplatz, setAlsStammplatz] = useState(false);
  const [zielWaehlen, setZielWaehlen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [anlegen, setAnlegen] = useState<Unbekannt | null>(null);

  const liste = useMemo(() => {
    if (bestand === undefined) return [];
    return art === "ausbuchen"
      ? ausbuchenAuswahl(bestand, lagerort_id)
      : einbuchenAuswahl(bestand, lagerort_id);
  }, [bestand, art, lagerort_id]);

  if (bestand === undefined) return <Ladeanzeige was="Der Inhalt" />;
  const hier = bestand.lagerorte.get(lagerort_id);
  if (hier === undefined) return null;

  // Ohne Netz ist der Schalter gesperrt -- und wirkt auch nicht, wenn das Netz
  // mitten im Packen wegbricht (ADR 0013 §6).
  const stammplatzFestlegen = art === "einbuchen" && alsStammplatz && online && !hier.standardziel;
  const wohin: Ziel | null =
    art === "einbuchen" ? { art: "lagerort", id: lagerort_id } : (ziel?.ziel ?? null);
  const alle = [...liste, ...dazu];
  const umschalten = (id: string): void => {
    setGewaehlt((alt) => {
      const neu = new Set(alt);
      if (neu.has(id)) neu.delete(id);
      else neu.add(id);
      return neu;
    });
  };

  if (anlegen !== null) {
    // Ein neues Geraet liegt hier: Es wird gleich in diesen Behaelter gebucht
    // (ADR 0026). Beim Ausbuchen ist es danach angekreuzt.
    const angelegt = (artikel_id: string, bezeichnung: string): void => {
      setAnlegen(null);
      if (art === "ausbuchen") setGewaehlt((alt) => new Set(alt).add(artikel_id));
      rueckmeldungGeben(true);
      setMeldung({
        art: "gut",
        kopf: `✓ ${bezeichnung} angelegt`,
        zusatz: `In ${hier.kennung} eingebucht${art === "ausbuchen" ? " und angekreuzt" : ""}.`,
      });
    };
    return (
      <GeraetAnlegen
        code={anlegen.code}
        vorschlag={anlegen.vorschlag}
        ort_id={lagerort_id}
        aufAbbruch={() => {
          setAnlegen(null);
        }}
        aufVorhanden={(artikel) => {
          setAnlegen(null);
          void aufCode(artikel.inventarnummer);
        }}
        aufFertig={(artikel) => {
          angelegt(artikel.id, artikel.bezeichnung);
        }}
      />
    );
  }

  if (zielWaehlen) {
    return (
      <Wohin
        titel={`Wohin ausbuchen? — aus ${hier.kennung}`}
        vorschlagOrt={lagerort_id}
        alsAusbuchenZiel
        ausschliessen={(z) =>
          z.art === "lagerort" && z.id === lagerort_id ? "das ist dieser Behälter selbst" : null
        }
        aufAbbruch={() => {
          setZielWaehlen(false);
        }}
        aufWahl={(z) => {
          aufZiel(z);
          setZielWaehlen(false);
          setMeldung(null);
        }}
      />
    );
  }

  /** Ein Scan kreuzt an -- oder bringt einen Artikel von woanders dazu. */
  const aufCode = async (code: string): Promise<void> => {
    let b = await bestandLaden();
    let gefunden = aufloesenIn(b, code);
    if (gefunden.art === "unbekannt") {
      gefunden = await codeAufloesen(code);
      b = await bestandLaden();
    }

    let artikel_id: string | null = null;
    if (gefunden.art === "lagerort") {
      const l = gefunden.lagerort;
      // Ein Packstück-QR meint die Kiste als Ding (ADR 0013 §5).
      artikel_id = l.artikel_id;
      if (artikel_id === null) {
        // Ein fester Ort: beim Ausbuchen das neue Ziel, sonst ein Hinweis.
        if (art === "ausbuchen" && l.id !== lagerort_id && !l.unbekannt) {
          aufZiel({ art: "lagerort", id: l.id });
          rueckmeldungGeben(true);
          setMeldung({
            art: "gut",
            kopf: `Ziel jetzt ${zielName(b, { art: "lagerort", id: l.id })}`,
          });
          return;
        }
        rueckmeldungGeben(false);
        setMeldung({
          art: "warnung",
          kopf:
            l.id === lagerort_id
              ? `Das ist ${hier.kennung} selbst`
              : `${l.kennung} ist ein fester Ort`,
          zusatz:
            l.id === lagerort_id ? undefined : (
              <Link to={`/lagerort/${l.id}`} className="font-semibold underline">
                {l.kennung} öffnen
              </Link>
            ),
        });
        return;
      }
    } else if (gefunden.art === "artikel") {
      artikel_id = gefunden.artikel.id;
    } else {
      rueckmeldungGeben(false);
      // Der Ausweg (ADR 0026): mit Netz anlegen, ohne Netz ist er gemerkt.
      const u = await unbekanntErfassen(gefunden.code, gefunden.vorschlag, lagerort_id);
      setMeldung({
        art: "warnung",
        kopf: unbekanntKopf(u),
        zusatz: (
          <UnbekannterCodeText
            unbekannt={u}
            aufAnlegen={() => {
              setMeldung(null);
              setAnlegen(u);
            }}
            aufVergessen={() => {
              setMeldung(null);
            }}
          />
        ),
      });
      return;
    }

    const artikel = b.artikel.get(artikel_id);
    const z = b.zustaende.get(artikel_id);
    if (artikel === undefined || z === undefined) return;
    if (artikel_id === hier.artikel_id) {
      rueckmeldungGeben(false);
      setMeldung({
        art: "warnung",
        kopf: `Das ist ${hier.kennung} selbst`,
        zusatz: "Die ganze Kiste geht über „Verschieben“.",
      });
      return;
    }
    if (art === "einbuchen" && z.lagerort_id === lagerort_id) {
      rueckmeldungGeben(false);
      setMeldung({ art: "still", kopf: `${artikel.bezeichnung} ist schon in ${hier.kennung}` });
      return;
    }

    const bekannt = alle.some((e) => e.artikel.id === artikel_id);
    if (!bekannt) {
      setDazu((alt) => [
        ...alt,
        {
          artikel,
          zustand: z,
          packstueck: ortDesArtikels(b, artikel.id),
          hinweis:
            art === "einbuchen"
              ? `war ${woWar(b, z)}`
              : `war nicht in ${hier.kennung}, sondern ${woWar(b, z)}`,
        },
      ]);
    }
    setGewaehlt((alt) => new Set(alt).add(artikel_id));
    rueckmeldungGeben(true);
    setMeldung({
      art: bekannt ? "gut" : "warnung",
      kopf: `✓ ${artikel.bezeichnung} angekreuzt`,
      zusatz: artikel.inventarnummer,
    });
  };

  /** „Fertig“: alles Angekreuzte in einem Vorgang buchen. */
  const fertig = async (): Promise<void> => {
    if (wohin === null) return;
    setLaeuft(true);
    try {
      const b = await bestandLaden();
      const vorgang = neuerVorgang();
      const gebucht: Gebucht[] = [];
      let geht = 0;
      for (const e of alle) {
        if (!gewaehlt.has(e.artikel.id)) continue;
        if (verboten(b, e.artikel.id, wohin)) {
          geht += 1;
          continue;
        }
        if (schonDort(b.zustaende.get(e.artikel.id), wohin)) continue;
        gebucht.push(await verschieben(b, e.artikel.id, wohin, vorgang));
        vorgang.stelle += 1;
      }

      let stammFehler: string | null = null;
      if (stammplatzFestlegen && gewaehlt.size > 0) {
        try {
          await stammplatzSetzen([...gewaehlt], lagerort_id);
        } catch (fehler) {
          stammFehler = fehler instanceof Error ? fehler.message : String(fehler);
        }
      }

      const wort = art === "einbuchen" ? "eingebucht in" : "ausgebucht nach";
      const namen = gebucht.map((g) => `${g.artikel.bezeichnung} · ${g.artikel.inventarnummer}`);
      const mehr = namen.length - NAMEN_IN_BESTAETIGUNG;
      rueckmeldungGeben(true);
      aufGebucht({
        art: geht > 0 || stammFehler !== null ? "warnung" : "gut",
        kopf: `✓ ${String(gebucht.length)} ${wort} ${zielName(b, wohin)}`,
        zusatz: (
          <>
            {namen.slice(0, NAMEN_IN_BESTAETIGUNG).map((n) => (
              <span key={n} className="block">
                {n}
              </span>
            ))}
            {mehr > 0 && <span className="block">… und {mehr} weitere</span>}
            {geht > 0 && (
              <span className="block">
                {geht} ausgelassen: Ein Packstück passt nicht in sich selbst.
              </span>
            )}
            {stammplatzFestlegen && stammFehler === null && (
              <span className="block">Stammplatz jetzt {hier.kennung}.</span>
            )}
            {stammplatzFestlegen && stammFehler === null && gewaehlt.size > 0 && (
              <EtikettHinweis bestand={b} artikel_ids={[...gewaehlt]} />
            )}
            {stammFehler !== null && (
              <span className="block">Stammplatz nicht gesetzt: {stammFehler}</span>
            )}
          </>
        ),
        gebucht,
      });
    } catch (fehler) {
      rueckmeldungGeben(false);
      setMeldung({
        art: "warnung",
        kopf: "Nicht gebucht",
        zusatz: fehler instanceof Error ? fehler.message : String(fehler),
      });
    } finally {
      setLaeuft(false);
    }
  };

  const anzahl = gewaehlt.size;
  const alleGewaehlt = liste.length > 0 && liste.every((e) => gewaehlt.has(e.artikel.id));

  return (
    <section
      aria-label={art === "einbuchen" ? "Einbuchen" : "Ausbuchen"}
      className="flex flex-col gap-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-bold">
          {art === "einbuchen" ? `Einbuchen in ${hier.kennung}` : `Ausbuchen aus ${hier.kennung}`}
        </h1>
        <Knopf art="still" onClick={aufAbbruch}>
          Abbrechen
        </Knopf>
      </div>

      {/* Das Ziel steht dauerhaft oben, mit „ändern“ (ADR 0013 §3). */}
      {art === "ausbuchen" && (
        <div
          data-testid="ausbuchen-ziel"
          className="flex items-center justify-between gap-2 rounded-xl border-2 border-blue-600 bg-blue-50 px-4 py-2 text-blue-950 dark:bg-blue-950 dark:text-blue-50"
        >
          <span>
            {ziel === null ? (
              <span className="font-semibold">Noch kein Ziel</span>
            ) : (
              <>
                nach <span className="font-bold">{zielName(bestand, ziel.ziel)}</span>
                <span className="block text-xs">
                  {ziel.zuletzt ? "zuletzt benutzt" : "Standard"}
                </span>
              </>
            )}
          </span>
          <Knopf
            onClick={() => {
              setZielWaehlen(true);
            }}
          >
            ändern
          </Knopf>
        </div>
      )}

      {/* Nur ausdrücklich gesetzt, standardmäßig aus, ohne Netz gesperrt (ADR 0013 §3, §6). */}
      {art === "einbuchen" && !hier.standardziel && (
        <div className="rounded-xl border-2 border-neutral-300 px-3 dark:border-neutral-600">
          <Schalter
            beschriftung="… und als Stammplatz festlegen"
            an={alsStammplatz && online}
            aendern={setAlsStammplatz}
            gesperrt={!online}
            hinweis={
              online
                ? `Was jetzt eingebucht wird, gehört künftig nach ${hier.kennung}.`
                : "Stammplatz ändern braucht Netz."
            }
          />
        </div>
      )}

      {meldung !== null && (
        <Rueckmeldung art={meldung.art} kopf={meldung.kopf} zusatz={meldung.zusatz} />
      )}
      <Scanner aufCode={(code) => void aufCode(code)} />

      <div className="flex items-center justify-between gap-2">
        <h2 className="font-bold">
          {art === "einbuchen" ? "Gehört hierher, ist aber woanders" : "Inhalt"} · {liste.length}
        </h2>
        {liste.length > 1 && (
          <Knopf
            onClick={() => {
              setGewaehlt((alt) => {
                const neu = new Set(alt);
                for (const e of liste) {
                  if (alleGewaehlt) neu.delete(e.artikel.id);
                  else neu.add(e.artikel.id);
                }
                return neu;
              });
            }}
          >
            {alleGewaehlt ? "keine" : "alle"}
          </Knopf>
        )}
      </div>

      {liste.length === 0 && (
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          {art === "einbuchen"
            ? "Nichts, was hierher gehört, ist woanders. Scannen bringt jeden Artikel dazu."
            : "Hier ist nichts gebucht. Scannen bringt Artikel von woanders dazu."}
        </p>
      )}

      <ul className="flex flex-col gap-1" aria-label="Auswahl">
        {liste.map((e) => (
          <AuswahlEintrag
            key={e.artikel.id}
            eintrag={e}
            an={gewaehlt.has(e.artikel.id)}
            umschalten={umschalten}
            zusatz={art === "einbuchen" ? woWar(bestand, e.zustand) : null}
            warnung={false}
          />
        ))}
      </ul>

      {dazu.length > 0 && (
        <>
          <h2 className="font-bold">Gescannt, von woanders · {dazu.length}</h2>
          <ul className="flex flex-col gap-1">
            {dazu.map((e) => (
              <AuswahlEintrag
                key={e.artikel.id}
                eintrag={e}
                an={gewaehlt.has(e.artikel.id)}
                umschalten={umschalten}
                zusatz={e.hinweis}
                warnung
              />
            ))}
          </ul>
        </>
      )}

      {/* Über der Fußleiste stehen bleiben -- auch bei einer langen Liste. */}
      <div className="sticky bottom-20 z-10 bg-neutral-100 py-2 dark:bg-neutral-950">
        <div className="grid">
          <Knopf
            art="haupt"
            disabled={anzahl === 0 || wohin === null || laeuft}
            onClick={() => void fertig()}
          >
            {laeuft
              ? "wird gebucht …"
              : anzahl === 0
                ? "Erst ankreuzen oder scannen"
                : `Fertig – ${String(anzahl)} ${art === "einbuchen" ? "einbuchen" : "ausbuchen"}`}
          </Knopf>
        </div>
      </div>
    </section>
  );
}

function AuswahlEintrag({
  eintrag,
  an,
  umschalten,
  zusatz,
  warnung,
}: {
  eintrag: AuswahlZeile;
  an: boolean;
  umschalten: (id: string) => void;
  zusatz: string | null;
  warnung: boolean;
}) {
  const { artikel, packstueck } = eintrag;
  return (
    <li>
      <label className="flex min-h-14 cursor-pointer items-center gap-3 rounded-xl bg-white px-3 py-2 active:bg-neutral-100 dark:bg-neutral-800 dark:active:bg-neutral-700">
        <input
          type="checkbox"
          className="size-7 shrink-0 accent-blue-700"
          checked={an}
          onChange={() => {
            umschalten(artikel.id);
          }}
        />
        <span className="flex min-w-0 flex-col">
          <span className="font-semibold">
            {packstueck === null
              ? artikel.bezeichnung
              : `${packstueck.kennung} — ${artikel.bezeichnung} (mit Inhalt)`}
          </span>
          <span className="flex flex-wrap items-center gap-x-3 text-sm">
            <Nummer>{artikel.inventarnummer}</Nummer>
            {zusatz !== null && (
              <span
                className={
                  warnung
                    ? "rounded bg-amber-100 px-1 text-amber-950 dark:bg-amber-950 dark:text-amber-100"
                    : "text-neutral-600 dark:text-neutral-300"
                }
              >
                {zusatz}
              </span>
            )}
          </span>
        </span>
      </label>
    </li>
  );
}
